import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AdjustmentsHorizontalIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  setParams,
  fetchBrandsByCategory,
  fetchModelsByBrand,
} from "../store/features/productsSlice";
import { Button, Input, Select } from "./ui";
import { toList } from "../utils/format";

// Select з варіантами { slug, name } і пунктом «Все»
const OptionsSelect = ({ label, value, onChange, items }) => (
  <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="">Все</option>
    {items.map((item) => (
      <option key={item.slug} value={item.slug}>
        {item.name}
      </option>
    ))}
  </Select>
);

const FilterSortBar = ({ onApply }) => {
  const dispatch = useDispatch();
  const productsState = useSelector((state) => state.products);

  const params = productsState.params;

  const categories = toList(productsState?.categories);
  const brandsByCategory = toList(productsState?.brandsByCategory);
  const modelsByBrand = toList(productsState?.modelsByBrand);

  const [open, setOpen] = useState(false);

  // Категорія, бренд і сортування застосовуються одразу — тож це просто фільтри з Redux
  // (і після повернення на головну тут видно ті самі значення, що й у «пігулках»).
  const category = params.category || "";
  const brand = params.brand || "";
  const ordering = params.ordering || "";

  // Модель і ціна — чернетка до «Применить»; заповнюється з фільтрів при відкритті
  const [model, setModel] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");

  const openFilters = () => {
    setModel(params.model || "");
    setPriceMin(params.price_min || "");
    setPriceMax(params.price_max || "");
    setOpen(true);
  };

  const setCategory = (value) => {
    if (value) dispatch(fetchBrandsByCategory(value));
    setModel("");
    dispatch(setParams({ category: value || null, brand: null, model: null }));
  };

  const setBrand = (value) => {
    if (value) dispatch(fetchModelsByBrand(value));
    setModel("");
    dispatch(setParams({ brand: value || null, model: null }));
  };

  const close = () => {
    setOpen(false);
    onApply?.();
  };

  const apply = () => {
    dispatch(
      setParams({
        model: model || null,
        ordering: ordering || null,
        price_min: priceMin || null,
        price_max: priceMax || null,
      })
    );
    close();
  };

  const reset = () => {
    setModel("");
    setPriceMin("");
    setPriceMax("");

    dispatch(
      setParams({
        category: null,
        brand: null,
        model: null,
        ordering: null,
        price_min: null,
        price_max: null,
      })
    );
    close();
  };

  return (
    <>
      {/* CONTROLS BAR */}
      <div className="flex gap-3">
        <Button variant="secondary" size="lg" className="shrink-0 px-5" onClick={openFilters}>
          <AdjustmentsHorizontalIcon className="w-5 h-5" />
          Фильтры
        </Button>

        <Select
          className="flex-1 min-w-0"
          aria-label="Сортировка"
          value={ordering}
          onChange={(e) => {
            dispatch(setParams({ ordering: e.target.value || null }));
            onApply?.();
          }}
        >
          <option value="">Сортировка</option>
          <option value="price">Дешево → Дорого</option>
          <option value="-price">Дорого → Дешево</option>
        </Select>
      </div>

      {/* FILTER MODAL */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end"
          onClick={() => setOpen(false)}
        >
          <div
            role="dialog"
            aria-label="Фильтры"
            className="w-full bg-slate-900 border-t border-slate-600 rounded-t-3xl p-6 pb-[calc(env(safe-area-inset-bottom)+24px)] max-h-[90vh] overflow-y-auto space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Фильтры</h3>
              <button
                aria-label="Закрыть"
                onClick={() => setOpen(false)}
                className="w-10 h-10 rounded-full bg-slate-800 border border-slate-600 flex items-center justify-center text-slate-300 hover:text-white"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <OptionsSelect label="Категория" value={category} onChange={setCategory} items={categories} />

            {brandsByCategory.length > 0 && (
              <OptionsSelect label="Бренд" value={brand} onChange={setBrand} items={brandsByCategory} />
            )}

            {modelsByBrand.length > 0 && (
              <OptionsSelect label="Модель" value={model} onChange={setModel} items={modelsByBrand} />
            )}

            <div>
              <p className="mb-2 text-sm font-medium text-slate-300">Цена, zł</p>
              <div className="flex gap-3">
                <Input
                  className="flex-1 min-w-0"
                  type="number"
                  inputMode="decimal"
                  placeholder="От"
                  aria-label="Цена от"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                />
                <Input
                  className="flex-1 min-w-0"
                  type="number"
                  inputMode="decimal"
                  placeholder="До"
                  aria-label="Цена до"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <Button variant="gradient" size="lg" className="flex-1" onClick={apply}>
                Применить
              </Button>
              <Button variant="secondary" size="lg" className="flex-1" onClick={reset}>
                Сброс
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FilterSortBar;
